/**
 * Gift matching pools page
 *
 * System Settings screen (system admin) listing the gift matching pools: one matching budget shared
 * by a gift match on each linked campaign (GR-4000).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Message, Segment, Table } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import CurrencyHelper from '../../../helpers/currencyHelper';
import { systemSettingsRoutes } from '../constants';
import { getGiftMatchingPools } from './api';
import { i18nKeys } from './constants';
import { accountLabel, formatPoolDate } from './helpers';
import { GiftMatchingPool } from './types';
import Styles from './GiftMatchingPools.module.css';

const GiftMatchingPools: React.FC = () => {
  const navigate = useNavigate();
  const [pools, setPools] = useState<GiftMatchingPool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setPools(await getGiftMatchingPools());
    } catch {
      setLoadError(I18n.t(i18nKeys.ERRORS.LOAD_FAILED));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className={Styles.header}>
        <h2>
          <Translate value={i18nKeys.TITLE} />
        </h2>
        <Button
          primary
          type="button"
          onClick={() =>
            navigate(systemSettingsRoutes.GIFT_MATCHING_POOLS_CREATE)
          }
        >
          <Translate value={i18nKeys.LIST.CREATE} />
        </Button>
      </div>

      <Segment>
        <p>
          <Translate value={i18nKeys.DESCRIPTION} />
        </p>
      </Segment>

      {loadError && <Message negative role="alert" content={loadError} />}
      {!isLoading && !loadError && pools.length === 0 && (
        <Message info content={I18n.t(i18nKeys.LIST.EMPTY)} />
      )}
      {pools.length > 0 && (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.NAME} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.STATUS} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.MULTIPLIER} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.CAMPAIGNS} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.MATCHED} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.UPDATED} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Translate value={i18nKeys.LIST.ACTIONS} />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pools.map((pool) => (
              <Table.Row key={pool.id}>
                <Table.Cell>{pool.name}</Table.Cell>
                <Table.Cell>
                  {I18n.t(pool.enabled ? i18nKeys.LIST.ON : i18nKeys.LIST.OFF)}
                </Table.Cell>
                <Table.Cell>
                  {I18n.t(i18nKeys.LIST.MULTIPLIER_VALUE, {
                    multiplier: pool.multiplier
                  })}
                </Table.Cell>
                <Table.Cell>{pool.campaigns.length}</Table.Cell>
                <Table.Cell>
                  {I18n.t(i18nKeys.LIST.MATCHED_VALUE, {
                    amount: CurrencyHelper.formatCurrency(
                      pool.donationMatchedTotalAmount,
                      pool.currency
                    ),
                    count: pool.donationMatchedCount
                  })}
                </Table.Cell>
                <Table.Cell>
                  {I18n.t(i18nKeys.LIST.UPDATED_VALUE, {
                    name:
                      accountLabel(pool.lastUpdatedBy) ??
                      I18n.t(i18nKeys.EDIT.UNKNOWN_ACCOUNT),
                    date: formatPoolDate(pool.lastUpdatedAt)
                  })}
                </Table.Cell>
                <Table.Cell>
                  <Button
                    basic
                    size="tiny"
                    type="button"
                    aria-label={`${I18n.t(i18nKeys.LIST.EDIT)} ${pool.name}`}
                    onClick={() =>
                      navigate(
                        `${systemSettingsRoutes.GIFT_MATCHING_POOLS_EDIT}/${pool.id}`
                      )
                    }
                  >
                    <Translate value={i18nKeys.LIST.EDIT} />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default GiftMatchingPools;
